import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import { useDefaultLayout } from "react-resizable-panels";
import { cn } from "../../../utils";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "../../base/resizable/resizable";
const GAP_CLASS = {
    sm: "gap-3",
    md: "gap-6",
    lg: "gap-8"
};
// In resizable mode the layout can't use flex `gap` (it inflates the
// PanelGroup's bounding rect relative to the panels' flex-distributed
// widths, so drag math overshoots). Mirror the gap inside the main
// panel via left-padding instead — padding doesn't change panel width.
const MAIN_INNER_PADDING_CLASS = {
    sm: "pl-3",
    md: "pl-6",
    lg: "pl-8"
};
export function DialogShellLayout({ sidebar, sidebarFooter, sidebarWidth, sidebarClassName, gap = "md", resizable = false, resizableAutoSaveId, sidebarDefaultSize = 20, sidebarMinSize = 12, sidebarMaxSize = 40, children, className }) {
    const hasSidebar = sidebar != null;
    // Hook must run unconditionally; the persisted layout is only wired up to
    // the group below when a save id was actually provided.
    const { defaultLayout, onLayoutChanged } = useDefaultLayout({
        id: resizableAutoSaveId ?? "dialog-shell-layout"
    });
    const sidebarInner = hasSidebar ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex-1 min-h-0",
                children: sidebar
            }),
            sidebarFooter && /*#__PURE__*/ _jsx("div", {
                className: "mt-3",
                children: sidebarFooter
            })
        ]
    }) : null;
    if (resizable && hasSidebar) {
        // `contain: layout` isolates each panel's layout work from the rest of
        // the tree, so a flex-grow change during drag doesn't force the browser
        // to recalculate ancestor layouts — keeps drag responsive when the main
        // column contains heavy content.
        return /*#__PURE__*/ _jsxs(ResizablePanelGroup, {
            orientation: "horizontal",
            defaultLayout: resizableAutoSaveId ? defaultLayout : undefined,
            onLayoutChanged: resizableAutoSaveId ? onLayoutChanged : undefined,
            className: cn("flex flex-1 min-h-0", className),
            children: [
                /*#__PURE__*/ _jsx(ResizablePanel, {
                    id: "dialog-shell-sidebar",
                    defaultSize: `${sidebarDefaultSize}%`,
                    minSize: `${sidebarMinSize}%`,
                    maxSize: `${sidebarMaxSize}%`,
                    style: {
                        contain: "layout"
                    },
                    children: /*#__PURE__*/ _jsx("div", {
                        className: cn("flex flex-col h-full animate-in fade-in-0 slide-in-from-left-2 duration-300", sidebarClassName),
                        children: sidebarInner
                    })
                }),
                /*#__PURE__*/ _jsx(ResizableHandle, {
                    className: "bg-transparent"
                }),
                /*#__PURE__*/ _jsx(ResizablePanel, {
                    id: "dialog-shell-main",
                    defaultSize: `${100 - sidebarDefaultSize}%`,
                    style: {
                        contain: "layout"
                    },
                    children: /*#__PURE__*/ _jsx("div", {
                        className: cn("flex flex-col h-full min-w-0", MAIN_INNER_PADDING_CLASS[gap]),
                        children: children
                    })
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: cn("flex flex-row flex-1 min-h-0", GAP_CLASS[gap], className),
        children: [
            hasSidebar && /*#__PURE__*/ _jsx("div", {
                className: cn("flex flex-col shrink-0 animate-in fade-in-0 slide-in-from-left-2 duration-300", sidebarClassName),
                style: sidebarWidth !== undefined ? {
                    width: sidebarWidth
                } : undefined,
                children: sidebarInner
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex-1 min-w-0 flex flex-col min-h-0",
                children: children
            })
        ]
    });
}

//# sourceMappingURL=DialogShellLayout.js.map